/**
 * THE TEXT PREVIEW PANEL — "what your family would get by text".
 *
 * A DEMO ONLY surface. It shows the real message the server built, with its
 * category label, its segment count, and the fact that nothing was sent
 * (a demo organization suppresses every text). A real household reads its
 * texts on its phone; the server returns an empty list for them.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

export default function GroceryTextPreview({ session, storeSession, side = 'shopper' }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState(null);
    const [failed, setFailed] = useState(false);

    const load = useCallback(() => {
        const url = storeSession
            ? `${API}/api/grocery/store/text-previews`
            : `${API}/api/grocery/text-previews`;
        const headers = storeSession
            ? { 'X-Grocery-Store-Session': storeSession }
            : { 'X-Grocery-Session': session };
        fetch(url, { headers })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`http_${r.status}`))))
            .then((d) => { setRows(d.previews || []); setFailed(false); })
            .catch(() => setFailed(true));
    }, [session, storeSession]);

    useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

    // "NOTHING TO SHOW" AND "COULD NOT BE READ" ARE DIFFERENT SENTENCES.
    if (failed && !rows?.length) {
        return (
            <p className="text-xs font-semibold text-muted-foreground"
                data-testid="grocery-text-preview-failed">{t('grocery.couldNotLoad')}</p>
        );
    }
    if (!rows || !rows.length) return null;

    return (
        <div className="space-y-2" data-testid="grocery-text-preview">
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                <MessageSquare className="h-3.5 w-3.5" />
                {t(side === 'store' ? 'groceryTexts.storeTitle' : 'groceryTexts.shopperTitle')}
            </p>
            {rows.map((p) => (
                <div key={p.id} className="rounded-2xl border bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
                    data-testid={`grocery-text-${p.event}`}>
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300"
                            data-testid={`grocery-text-category-${p.event}`}>
                            {t(`groceryTexts.category.${p.category}`)}
                        </span>
                        <span>{t('groceryTexts.to', { name: p.to_name || p.to_masked })}</span>
                        <span>{new Date(p.at).toLocaleTimeString()}</span>
                        <span data-testid={`grocery-text-segments-${p.event}`}>
                            {t('groceryTexts.segments', { count: p.segments })}
                        </span>
                    </p>
                    <p className="mt-2 whitespace-pre-line rounded-2xl bg-slate-100 px-3 py-2 text-sm text-slate-900 dark:bg-slate-800 dark:text-slate-100"
                        data-testid={`grocery-text-body-${p.event}`}>{p.body}</p>
                    <p className="mt-1 text-[11px] text-slate-500" data-testid={`grocery-text-status-${p.event}`}>
                        {p.status === 'suppressed_demo'
                            ? t('groceryTexts.notSent') : t('groceryTexts.sentStatus', { status: p.status })}
                    </p>
                </div>
            ))}
        </div>
    );
}
